/**
 * Copyright (c) 2020 TechDivision GmbH
 * All rights reserved
 */

/**
 * @module StudioSelect
 */
(function StudioSelect() {
    /**
     * handles the events for the studio select
     *
     * @constructor
     */
    function StudioSelect(studioWrapper) {
        /**
         * // local object availability
         * @type {StudioSelect}
         * @private
         */
        var _this = this;

        /**
         * Is set to element node if it's a studio page
         * @type {Element | null}
         */
        var studioPageNode = document.querySelector('.js-studio-page');

        /**
         * @type {StudioFavourite}
         * @type {null}
         */
        var favourite = null;

        /**
         * @type {element}
         */
        this.studioWrapper = studioWrapper;

        /**
         * @type {element}
         */
        this.studioSelectComponent = studioWrapper.querySelector('.js-studio-select');

        /**
         * @type {element}
         */
        this.studioSelectDropdownComponent = studioWrapper.querySelector('.js-studio-select-dropdown');

        /**
         * Bind all needed events for the StudioSelector
         *
         * @method bindEvents
         */
        this.bindEvents = function () {
            this.studioSelectComponent.querySelector('select').addEventListener('change', this.selectOptionCB);
            this.studioWrapper.querySelector('.js-studio-select__bookmark').addEventListener('click', this.bookmarkStudioCB);
            this.studioWrapper.querySelector('.js-studio-select__bookmarked').addEventListener('click', this.removeBookmarkStudioCB);
        };

        /**
         * This function will be called, if the user selects a new option
         */
        this.selectOptionCB = function () {
            let selectedOption = _this.getSelectedOption();
            if(selectedOption.value) {
                _this.redirectToTarget(selectedOption);
            } else {
                // Since no valid studio is selected, Hide all action buttons
                _this.hideStudioActions();
            }
        };

        /**
         * Redirects to target from data attribute of given option
         *
         * @method redirectTarget
         * @param {object | null} selectedOption
         */
        this.redirectToTarget = function (selectedOption) {
            let targetUrl = selectedOption.dataset.target;
            if(targetUrl) {
                window.location.href = targetUrl;
            }
        };

        /**
         * Bookmark the current select studio. This function will be called, if the user clicks the bookmark button.
         *
         * @param event
         */
        this.bookmarkStudioCB = function (event) {
            let selectedOption = _this.getSelectedOption();
            if(selectedOption && selectedOption.value) {
                _this.getStudioFavourite().setBookmark(selectedOption.value);
                _this.setBookMarkButtonState(true);
            }
            event.preventDefault();
        };

        /**
         * Remove the bookmarked studio
         *
         * @param event
         */
        this.removeBookmarkStudioCB = function (event) {
            _this.getStudioFavourite().setBookmark(null);
            _this.setBookMarkButtonState(false);
            event.preventDefault();
        };

        /**
         * Get the selected option
         *
         * @returns {object | null}
         */
        this.getSelectedOption = function () {
            let selectNode = _this.studioSelectComponent.querySelector('select');
            return selectNode.options[selectNode.selectedIndex];
        };

        /**
         * Pre choose an option depending on context
         */
        this.preChooseStudio = function() {
            let studioIdentifier = null;
            let currentPageIsAStudioPage = studioPageNode != null;
            // checks if current page is studio page
            if(currentPageIsAStudioPage) {
                // current page is studio page
                studioIdentifier = studioPageNode.dataset.studioId
            } else {
                // current page is no studio page, try to get a favourite
                let favourite = new window.Jumpers.StudioFavourite();
                studioIdentifier = favourite.getLikelyStudioIdentifier();
            }

            // if no likely studio identifier could be returned
            if(!studioIdentifier) {
                return false;
            }

            let selectNode = this.studioSelectComponent.querySelector('select');
            let optionNodes = selectNode.querySelectorAll('option');
            let selectedStudioName = '';

            // search for matching option
            optionNodes.forEach(function(element, index) {
                // weak compare to ensure it also works if given identifier is integer or string
                if(element.value == studioIdentifier) {
                    selectNode.selectedIndex = index;
                    selectedStudioName = element.text;
                    //If we are not on a studio page show a link to the studio
                    if(!currentPageIsAStudioPage) {
                        _this.setLinkToStudio(element);
                    }
                }
            });

            _this.setDropdownMenuButtonLabel(selectedStudioName);
        };

        /**
         * Set the target url for the selected studio page and activate the studio link
         *
         * @param {object | null} selectOption
         */
        this.setLinkToStudio = function(selectOption) {
            // Only display the button on pages that are no studio page
            if(selectOption.dataset.target && studioPageNode === null) {
                bookmarkLink = _this.studioWrapper.querySelector('.js-studio-select__link-to-studio');
                bookmarkLink.setAttribute('href', selectOption.dataset.target);
                bookmarkLink.classList.remove("hidden");
            }
        };

        /**
         * Set current bookmark state
         */
        this.setBookMarkState = function() {
            let selectedOption = _this.getSelectedOption();
            // Show the bookmark action only, if any studio is selected
            if(selectedOption.value) {
                //Check if a studio is bookmarked and it's the same as the current selected one
                let bookMarkState = _this.getStudioFavourite().isBookmarked() && selectedOption.value === _this.getStudioFavourite().getLikelyStudioIdentifier();
                _this.setBookMarkButtonState(bookMarkState);
            }
        };

        /**
         * Set the state of the bookmark button
         *
         * @param {boolean} bookmarked
         */
        this.setBookMarkButtonState = function(bookmarked) {
            let bookmarkButton = _this.studioWrapper.querySelector('.js-studio-select__bookmark');
            let bookmarkedButton = _this.studioWrapper.querySelector('.js-studio-select__bookmarked');
            //Switch bookmark link
            if(bookmarked) {
                bookmarkButton.classList.add("hidden");
                bookmarkedButton.classList.remove("hidden");
            } else {
                bookmarkButton.classList.remove("hidden");
                bookmarkedButton.classList.add("hidden");
            }
        };

        /**
         * Set the label of the dropdown menu used for desktop
         * @param selectedStudioName
         */
        this.setDropdownMenuButtonLabel = function(selectedStudioName) {
            if (selectedStudioName !== '' && selectedStudioName !== null) { // set name for dropdown menu button
                let dropdownButton = this.studioSelectDropdownComponent.querySelector('.js-studio-select-dropdown-button');
                dropdownButton.textContent = selectedStudioName;
            }
        }

        /**
         * Hide all studio actions
         */
        this.hideStudioActions = function() {
            let studioActions = _this.studioWrapper.querySelectorAll('.js-studio-select__action');
            // search for matching option
            studioActions.forEach(function(element, index) {
                if(!element.classList.contains('hidden')) {
                    element.classList.add("hidden");
                }
            });
        };

        /**
         * Returns the studio favorite object
         *
         * @returns {StudioFavourite}
         */
        this.getStudioFavourite = function() {
            if(_this.favourite == null) {
                _this.favourite = new window.Jumpers.StudioFavourite();
            }
            return _this.favourite;
        };

        /**
         * @method init
         */
        this.init = function () {
            this.preChooseStudio();
            this.setBookMarkState();
            this.bindEvents();
        };

        /**
         * initialize call
         */
        this.init();
    }

    // define the global scope
    if (!window.Jumpers) {
        window.Jumpers = {};
    }

    window.Jumpers.StudioSelect = StudioSelect;
})();

function studioPageReady(fn) {
    if (document.readyState != 'loading') {
        fn();
    } else if (document.addEventListener) {
        document.addEventListener('DOMContentLoaded', fn);
    } else {
        document.attachEvent('onreadystatechange', function () {
            if (document.readyState != 'loading')
                fn();
        });
    }
}

function initJumpersStudioSelect() {
    var studioSelectWrapper = document.querySelector('.js-studio-select-wrapper');

    if (studioSelectWrapper) {
        new window.Jumpers.StudioSelect(studioSelectWrapper);
    }
}

studioPageReady(initJumpersStudioSelect);
